import { parseChord } from './chord';
import { resolvedChordValue, sameResolvedChord } from './chordIdentity';
import { defaultKeybindings, keybindingCommandIds } from './defaultKeybindings';
import { findReservedChord, isScopedReservedException } from './reservedChords';
import type {
  KeybindingCommandId,
  KeybindingNotice,
  KeybindingScope,
  KeyboardLayoutSnapshot,
  Keymap,
  Platform,
} from './keybindingTypes';

type MutableKeymap = Record<KeybindingCommandId, string[]>;

export function createKeybindingNotice(
  kind: KeybindingNotice['kind'],
  commandId: KeybindingCommandId,
  chord: string,
  message: string,
  persisted: boolean,
  relatedCommandId?: KeybindingCommandId,
  relatedChord?: string,
): KeybindingNotice {
  return {
    kind,
    severity: kind === 'reserved' ? 'error' : 'warning',
    commandId,
    chord,
    message,
    persisted,
    relatedCommandId,
    ...(relatedChord ? { relatedChord } : {}),
  };
}

/** True when `chord` is one the current mutation added to `commandId` (all of its chords when `mutatedChords` is omitted). */
export function isAddedByMutation(
  commandId: KeybindingCommandId,
  chord: string,
  mutatedCommandId?: KeybindingCommandId,
  mutatedChords?: readonly string[],
): boolean {
  return commandId === mutatedCommandId && (!mutatedChords || mutatedChords.includes(chord));
}

const MONACO_DEFAULTS = [
  'Mod+F', 'Mod+H', 'Mod+D', 'Mod+G', 'Mod+Enter', 'Mod+Shift+Enter',
  'Shift+Enter', 'Mod+/', 'Mod+Space', 'F1', 'F2', 'F8', 'F12',
];

/** Scopes whose surface hosts a Monaco editor, so a tab-scoped chord can fire while it has text focus. */
const MONACO_HOST_SCOPES: ReadonlySet<KeybindingScope> = new Set<KeybindingScope>([
  'query-editor', 'sql-editor', 'notebook', 'plsql-editor', 'debugger',
]);

/**
 * Every command that can fire while a Monaco editor has text focus, and so
 * can take a chord away from the editor: editor-focus commands, global
 * commands that work anywhere, and tab/anywhere commands of a scope that
 * hosts an editor.
 */
function runsWhileMonacoHasFocus(commandId: KeybindingCommandId): boolean {
  const { focus, scope } = defaultKeybindings[commandId];
  if (focus === 'sql-editor') return true;
  if (scope === 'global') return focus === 'anywhere';
  return (focus === 'tab' || focus === 'anywhere') && MONACO_HOST_SCOPES.has(scope);
}

export function findWarnings(
  commandId: KeybindingCommandId,
  chords: readonly string[],
  platform: Platform,
): KeybindingNotice[] {
  const definition = defaultKeybindings[commandId];
  const warnings: KeybindingNotice[] = [];
  for (const chord of chords) {
    const parsed = parseChord(chord);
    if (!parsed) continue;
    if (
      runsWhileMonacoHasFocus(commandId) &&
      MONACO_DEFAULTS.some((item) => sameResolvedChord(item, chord, platform))
    ) {
      warnings.push(createKeybindingNotice(
        'monaco-shadow', commandId, chord,
        `${chord} shadows a Monaco editor shortcut in this scope.`, false,
      ));
    }
    const resolved = resolvedChordValue(parsed, platform) ?? '';
    if (
      platform !== 'mac' && definition.scope === 'global' &&
      definition.worksInTerminal && /^Ctrl(?:\+Shift)?\|logical:[A-Z]$/.test(resolved)
    ) {
      warnings.push(createKeybindingNotice(
        'terminal-shell-key', commandId, chord,
        `${chord} may shadow a shell key while the terminal is focused.`, false,
      ));
    }
    // Option types a character only without ⌘ or ⌃ (Shift may be held too).
    const optionTypesCharacter = parsed.modifiers.includes('Alt') &&
      !parsed.modifiers.some((modifier) => modifier === 'Mod' || modifier === 'Cmd' || modifier === 'Ctrl');
    if (platform === 'mac' && optionTypesCharacter && /^[A-Z]$/.test(parsed.key)) {
      warnings.push(createKeybindingNotice(
        'mac-option', commandId, chord,
        'Option combinations type characters in text fields; this binding only applies in its scope.',
        false,
      ));
    }
  }
  return warnings;
}

/**
 * Reserved chords (D6). A reserved chord is refused durably only when the
 * current mutation ADDED it to its command (`mutatedChords`; when omitted,
 * every chord of `mutatedCommandId` counts as added). Any other reserved chord
 * — found on hydration, an import, a scheme switch, a layout re-validation,
 * another command's change, or already stored on the row being edited — is
 * only suppressed, with a non-durable notice, and stays stored: a settings
 * file from another platform (F11 on macOS, reserved for full screen on
 * Windows/Linux) must survive a round trip and later edits of that row.
 */
export function applyReservedChordPass(
  initial: Keymap,
  platform: Platform,
  layoutMap: KeyboardLayoutSnapshot,
  isDev: boolean,
  mutatedCommandId?: KeybindingCommandId,
  mutatedChords?: readonly string[],
): {
  readonly working: MutableKeymap;
  readonly durable: MutableKeymap;
  readonly notices: KeybindingNotice[];
  readonly durableChanges: Set<KeybindingCommandId>;
} {
  const durable = Object.fromEntries(
    keybindingCommandIds.map((id) => [id, [...initial[id]]]),
  ) as MutableKeymap;
  const working = Object.fromEntries(
    keybindingCommandIds.map((id) => [id, [...initial[id]]]),
  ) as MutableKeymap;
  const notices: KeybindingNotice[] = [];
  const durableChanges = new Set<KeybindingCommandId>();

  for (const commandId of keybindingCommandIds) {
    working[commandId] = working[commandId].filter((chord) => {
      const reserved = findReservedChord(chord, platform, isDev, layoutMap);
      if (!reserved) return true;
      if (reserved.layoutDependent) {
        const resolution = reserved.layoutUnknown ? 'may resolve to' : 'resolves to';
        notices.push(createKeybindingNotice(
          'layout-collision', commandId, chord,
          `${chord} ${resolution} the reserved ${reserved.role ?? 'operating-system'} shortcut for this keyboard layout.`,
          false,
        ));
        return false;
      }
      if (isScopedReservedException(commandId, chord, platform, isDev)) {
        notices.push(createKeybindingNotice(
          'reserved-scope-exception', commandId, chord,
          `${chord} shadows the ${reserved.role ?? 'operating-system'} shortcut only in this scope.`,
          false,
        ));
        return true;
      }
      const refused = isAddedByMutation(commandId, chord, mutatedCommandId, mutatedChords);
      notices.push(createKeybindingNotice(
        'reserved', commandId, chord,
        refused
          ? `${chord} is reserved by ${reserved.role ?? 'the operating system'}.`
          : `${chord} is reserved by ${reserved.role ?? 'the operating system'} on this platform; it stays saved but is inactive.`,
        refused,
      ));
      if (refused) {
        durable[commandId] = durable[commandId].filter((item) => item !== chord);
        durableChanges.add(commandId);
      }
      return false;
    });
  }

  return { working, durable, notices, durableChanges };
}
